import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ExportLinks from "./ExportLinks";
import { getAvatarUrl } from "../utils/avatarURL";
import { getStatusClass } from "../utils/calendarUtils";
import { formatFullDate, getNestedValue } from "../utils/tableUtils";
import { capitalizeText } from "../utils/stringUtils";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useSelector } from "react-redux";
import { canEditForms } from "../constants/rolePresets";
import InventoryModal from "../../pages/private/FormDetails/Modals/InventoryModal";
import InventoryTransactionModal from "../../pages/private/FormDetails/Modals/InventoryTransactionModal";
import TableFilters from "../../pages/private/FormDetails/TableUtils/TableFilters";
import { dateFilters } from "../../pages/private/Dashboard/utils/dateFilters";
import CommonTableSkeleton from "./skeleton/CommonTableSkeleton";

const TableList = ({ data, columns, loading }) => {
  const [search, setSearch] = useState("");
  const [entries, setEntries] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [showInventoryModal, setShowInventoryModal] = useState(false);
  const [showInventoryTransactionModal, setShowInventoryTransactionModal] =
    useState(false);

  const [dateFilter, setDateFilter] = useState("All");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");

  const { tablename } = useParams();
  const navigate = useNavigate();

  const userInfo = useSelector((state) => state.user.userInfo);

  // 🧭 Sorting state — default: created_on, descending
  const [sortConfig, setSortConfig] = useState({
    key: "created_on",
    direction: "desc",
  });

  useEffect(() => {
    const calculateEntries = () => {
      const rowHeight = 38; // approximate table row height (adjust if needed)
      const headerHeight = 280; // includes filters + search area
      const availableHeight = window.innerHeight - headerHeight;

      // Rough estimate: how many rows can fit in remaining space
      const newEntries = Math.max(5, Math.floor(availableHeight / rowHeight));
      setEntries(newEntries);
    };

    // Run on load and when resizing window
    calculateEntries();
    window.addEventListener("resize", calculateEntries);

    return () => window.removeEventListener("resize", calculateEntries);
  }, []);

  /** 🔁 Handle sorting click */
  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        // Toggle direction
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      } else {
        // New column: default to ascending
        return { key, direction: "asc" };
      }
    });
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({});
    setDateFilter("All");
    setCurrentPage(1);
    setSearch("");
    setShowFilters(false);
  };

  // 🔍 Filtering logic
  const filteredData = data.filter((item) => {
    // text search
    const matchesSearch = columns.some((col) => {
      const value = getNestedValue(item, col.name);
      return String(value || "")
        .toLowerCase()
        .includes(search.toLowerCase());
    });

    // field filters
    const matchesFilters = Object.entries(filters).every(([key, val]) => {
      if (!val) return true;
      const rawValue = getNestedValue(item, key);
      if (!rawValue) return false;

      // detect field type from filterConfig
      const fieldConfig = columns.find((f) => f.key === key);
      const type = fieldConfig?.type || "text";

      // exact match for select fields
      if (type === "select") {
        return String(rawValue) === String(val);
      }

      const isDate = key.toLowerCase().includes("date");
      if (isDate) {
        const recordDate = new Date(rawValue).toISOString().split("T")[0];
        return recordDate === val;
      }

      return String(rawValue).toLowerCase().includes(String(val).toLowerCase());
    });

    return matchesSearch && matchesFilters;
  });

  const parsePHDate = (str, endOfDay = false) => {
    const [y, m, d] = str.split("-").map(Number);
    const date = new Date(y, m - 1, d);

    if (endOfDay) {
      date.setHours(23, 59, 59, 999); // FULL day
    }

    return date;
  };

  // Date filtering
  const applyDateFilter = (data) => {
    if (dateFilter === "All") return data;

    let start = null;
    let end = null;

    if (dateFilter === "Custom Range" && customStart && customEnd) {
      start = parsePHDate(customStart);
      end = parsePHDate(customEnd, true);
    } else if (dateFilters[dateFilter]) {
      [start, end] = dateFilters[dateFilter]();
    }

    return data.filter((t) => {
      const created = new Date(t.created_on);
      return !start || (created >= start && created <= end);
    });
  };

  const filteredByDate = applyDateFilter(filteredData);

  // 📊 Sorting logic
  const sortedData = [...filteredByDate].sort((a, b) => {
    const aVal = getNestedValue(a, sortConfig.key);
    const bVal = getNestedValue(b, sortConfig.key);

    if (aVal == null) return 1;
    if (bVal == null) return -1;

    // Numeric or Date comparison
    const aNum = !isNaN(Date.parse(aVal)) ? new Date(aVal).getTime() : aVal;
    const bNum = !isNaN(Date.parse(bVal)) ? new Date(bVal).getTime() : bVal;

    if (aNum < bNum) return sortConfig.direction === "asc" ? -1 : 1;
    if (aNum > bNum) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  // if (sortedData.length < entries) {
  //   setEntries(sortedData.length);
  // }

  const startIndex = (currentPage - 1) * entries;
  const endIndex = startIndex + entries;
  const paginatedData = sortedData.slice(startIndex, endIndex);

  const handleCreateNew = () => {
    if (tablename === "inventory") {
      setShowInventoryModal(true);
    } else if (tablename === "inventoryLogs") {
      setShowInventoryTransactionModal(true);
    } else {
      navigate(`/form/${tablename}/create`);
    }
  };

  const handleRowClick = (item) => {
    navigate(`/form/${tablename}/view/${item._id}`);
  };

  const canEdit = userInfo && canEditForms.includes(userInfo.role);

  // Compute dynamic columns with options for reference fields
  const dynamicColumns = React.useMemo(() => {
    return columns.map((col) => {
      const newCol = { ...col };

      // Fields to check for dynamic options
      const referenceFields = ["item.", "created_by.", "patient.", "doctor."];

      // Check if column starts with any reference field
      const matchedField = referenceFields.find((prefix) =>
        col.name.startsWith(prefix)
      );

      if (matchedField) {
        const fieldKey = col.name.split(".")[1]; // e.g., "item.name" -> "name"

        // Collect unique values from the data
        const uniqueValues = [
          ...new Set(
            data
              .map((row) => row[matchedField.slice(0, -1)]?.[fieldKey])
              .filter(Boolean)
          ),
        ];

        // Assign options with "All" default
        newCol.options = [...uniqueValues.map((v) => ({ value: v, label: v }))];
      }

      return newCol;
    });
  }, [columns, data]);

  return (
    <>
      <div className="px-4 py-2 shadow-lg bg-white rounded-lg overflow-hidden h-full mx-auto  ">
        {/* 🔍 Search + Create */}
        <div className="table-responsive mb-4 flex flex-col h-full">
          <div className="flex-grow flex-1 overflow-x-auto">
            <div className="flex justify-between mb-2 items-center">
              <div className="flex items-center space-x-2 text-sm">
                <label>Show</label>
                <select
                  value={entries}
                  onChange={(e) => setEntries(parseInt(e.target.value, 10))}
                  className="border border-gray-300 rounded-md px-2 py-1 text-sm"
                >
                  {(() => {
                    // Default options
                    const options = [10, 25, 50, 100];

                    // Add dynamic entry count if not already in the list
                    if (!options.includes(entries)) options.push(entries);

                    // ✅ Sort ascending
                    options.sort((a, b) => a - b);

                    return options.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ));
                  })()}
                </select>
                <label>entries</label>
              </div>
              <div className="flex justify-end items-center">
                <ExportLinks data={sortedData} />
                {canEdit && (
                  <button
                    onClick={handleCreateNew}
                    className="bg-green-500 ml-4 px-4 py-2 rounded-md text-white text-sm font-medium hover:bg-green-600 transition"
                  >
                    Create New
                  </button>
                )}
              </div>
            </div>

            <TableFilters
              searchTerm={search}
              setSearchTerm={setSearch}
              filters={filters}
              setFilters={setFilters}
              filterConfig={dynamicColumns}
              showFilters={showFilters}
              setShowFilters={setShowFilters}
              clearFilters={clearFilters}
              dateFilter={dateFilter}
              setDateFilter={setDateFilter}
              customStart={customStart}
              setCustomStart={setCustomStart}
              customEnd={customEnd}
              setCustomEnd={setCustomEnd}
              setPage={setCurrentPage}
            />

            {/* Table */}
            <table className="min-w-full table-auto border-collapse text-sm">
              <thead>
                <tr className="border-b bg-gray-50">
                  {columns.map((col) => (
                    <th
                      key={col.name}
                      onClick={() => handleSort(col.name)}
                      className="p-2 font-semibold text-gray-600 text-left cursor-pointer select-none hover:bg-gray-100"
                    >
                      <div className="flex items-center gap-1">
                        {col.label}
                        {sortConfig.key === col.name &&
                          (sortConfig.direction === "asc" ? (
                            <ChevronUp size={14} />
                          ) : (
                            <ChevronDown size={14} />
                          ))}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <CommonTableSkeleton columns={columns} rows={entries} />
                ) : (
                  paginatedData.map((item, i) => (
                    <tr
                      key={item._id}
                      onClick={() => handleRowClick(item)}
                      className={`border-b hover:bg-side-active cursor-pointer ${
                        i % 2 === 0 ? "" : "bg-white"
                      }`}
                    >
                      {columns.map((col) => {
                        const val = getNestedValue(item, col.name);
                        return (
                          <td
                            key={col.name}
                            className="p-2 truncate max-w-[180px]"
                          >
                            {col.name === "status" ||
                            col.name === "role" ||
                            col.name === "user_type" ? (
                              <span
                                className={`px-2 py-1 rounded-md text-xs ${getStatusClass(
                                  val?.toLowerCase()
                                )}`}
                              >
                                {capitalizeText(val, true)}
                              </span>
                            ) : col.name === "created_on" ? (
                              val
                            ) : col.name.toLowerCase().includes("date") ? (
                              formatFullDate(val)
                            ) : col.name === "avatar" ? (
                              <img
                                src={getAvatarUrl(val)}
                                alt="Avatar"
                                className="w-8 h-8 rounded-full"
                              />
                            ) : (
                              val || "-"
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex justify-between mt-4 items-center text-sm text-gray-600">
            <div>
              Showing {startIndex + 1} to{" "}
              {Math.min(endIndex, sortedData.length)} of {sortedData.length}{" "}
              entries
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded-md bg-gray-100 hover:bg-gray-200 disabled:bg-gray-200 disabled:cursor-not-allowed transition"
              >
                Previous
              </button>
              <button
                onClick={() =>
                  setCurrentPage((p) =>
                    p * entries < sortedData.length ? p + 1 : p
                  )
                }
                disabled={currentPage * entries >= sortedData.length}
                className="px-3 py-1 border rounded-md bg-gray-100 hover:bg-gray-200 disabled:bg-gray-200 disabled:cursor-not-allowed transition"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {tablename === "inventory" && showInventoryModal && (
        <InventoryModal onClose={() => setShowInventoryModal(false)} />
      )}

      {tablename === "inventoryLogs" && showInventoryTransactionModal && (
        <InventoryTransactionModal
          onClose={() => setShowInventoryTransactionModal(false)}
        />
      )}
    </>
  );
};

export default TableList;
